<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify'
import { useRoute } from 'vue-router';
import { computed, markRaw, ref, watch } from 'vue';

import DividerComponent from '@/components/ui/DividerComponent.vue';
import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import RefuseMandateComponent from '@/components/mandate/RefuseMandateComponent.vue';
import ManagementReportUploadComponent from '@/components/mandate/ManagementReportUploadComponent.vue';
import MandateService from '@/services/mandate/mandateService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import {
  formatCurrency,
  formatDate,
  formatDateOnly,
  formatItemValue,
  formatPercentage,
  formatMandateBoStatus,
  formatMandatePublicStatus,
} from '@/utils/formatters.ts';
import {
  MandatePublicStatusEnum,
  MandateBoStatusEnum,
  MandateDocumentCategoryEnum
} from "@/enums/mandate_enum.ts";

import type { DataTableHeader } from 'vuetify/framework';
import type {
  AssetRow,
  MandateDocument,
  MandateRefuseRequest,
  MandateResponse,
  MandatePublicStatusKey,
  MandateInvestmentResponse
} from '@/types/services/mandate/mandate.type.ts';

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const { smAndDown } = useDisplay()

const step = ref<number>(1)
const loading = ref<boolean>(false)
const mandate = ref<MandateResponse>({ ...(route.meta.mandate as MandateResponse) })
const investment = ref<MandateInvestmentResponse>({
  totalInvestment: 0,
  userInvestments: [],
  page: 1,
  limit: 10,
  total: 0,
  maxPages: 1
})
if (!authStore.isAdmin) {
  const rawInvestment = route.meta.investment as MandateInvestmentResponse
  investment.value = {
    ...rawInvestment,
    totalInvestment: rawInvestment.totalInvestment,
    userInvestments: rawInvestment.userInvestments
  }
}

const limit = ref<number>(10)
const page = ref<number>(1)
const total = ref<number>(investment.value.total ?? 0)
const maxPages = ref<number>(investment.value.maxPages ?? 1)

const assetHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.detailMandate.table.asset.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.asset.percentage"),
    key: "percentage",
    align: "start",
    sortable: false
  }
])
const documentHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.detailMandate.table.document.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.document.category"),
    key: "category",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.document.createdAt"),
    key: "createdAt",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.document.action"),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])
const userInvestmentHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t('common.userList.header.email'),
    key: 'email',
    sortable: false,
    align: 'start'
  },
  {
    title: t('common.userList.header.lastName'),
    key: 'lastName',
    sortable: false,
    align: 'start'
  },
  {
    title: t('common.userList.header.firstName'),
    key: 'firstName',
    sortable: false,
    align: 'start'
  },
  {
    title: t('common.userList.header.investment'),
    key: 'amount',
    sortable: false,
    align: 'start'
  }
])

const totalPercentage = computed<number>(() => {
  const assets: AssetRow[] = mandate.value.assets
  return assets.reduce((sum, asset) => sum + (Number(asset.percentage) || 0), 0)
})

const isPending = computed( () =>
  mandate.value.boStatus === MandateBoStatusEnum.PENDING ||
  mandate.value.boStatus === MandateBoStatusEnum.PENDING_DIC
)
const isDraft = computed(() => mandate.value.boStatus === MandateBoStatusEnum.DRAFT)

const canValidateMandate = computed(() =>
  authStore.isAdmin &&
  isPending.value &&
  hasPermission(['VALIDATE_MANDATE'])
)

const canCloseMandate = computed(() =>
  authStore.isAdmin &&
  mandate.value.publicStatus === MandatePublicStatusEnum.OPENED &&
  !isPending.value &&
  hasPermission(['ADMIN_CLOSE_MANDATE'])
)

const canReactiveMandate = computed(() =>
  authStore.isAdmin &&
  (mandate.value.publicStatus === MandatePublicStatusEnum.PRIVATE ||
    mandate.value.publicStatus === MandatePublicStatusEnum.CLOSED) &&
  !mandate.value.custody &&
  !isDraft.value &&
  !isPending.value &&
  hasPermission(['VALIDATE_MANDATE'])
)

watch([limit, page], getUserInvestment);

function categoryLabel(category: string) {
  return t(`views.detailMandate.table.document.label.${category}`)
}

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function downloadDocument(mandateDocument: MandateDocument) {
  if (!mandateDocument.id) {
    return
  }

  const url = await MandateService.downloadDocument(mandate.value.id, mandateDocument.id)
  window.open(url, '_blank')
}

async function refusedMandate() {
  const modal = await displayModal({
    component: markRaw(RefuseMandateComponent),
    props: {
      mandateName: mandate.value.name
    },
  });

  if (modal && typeof modal === 'object') {
    const { reason } = modal as MandateRefuseRequest;
    await MandateService.refuseMandate(mandate.value.id, reason);
    displayToast(t('views.detailMandate.refusalSuccess'), 'success');
    mandate.value.boStatus = MandateBoStatusEnum.REFUSED;
  }
}

async function updateStatus(status: MandatePublicStatusKey) {
  await MandateService.updateStatus(mandate.value.id, status)
  mandate.value.publicStatus = status

  if (status === MandatePublicStatusEnum.OPENED) {
    displayToast(t('views.detailMandate.validationSuccess'), 'success');
    if (mandate.value.boStatus !== MandateBoStatusEnum.REFUSED) {
      mandate.value.boStatus = MandateBoStatusEnum.VALIDATED;
    }
  } else if (status === MandatePublicStatusEnum.PRIVATE) {
    displayToast(t('views.detailMandate.closeSuccess'), 'success');
  } else {
    displayToast(t('views.detailMandate.reactivateSucces'), 'success');
  }
}

async function getUserInvestment(): Promise<void> {
  try {
    loading.value = true;

    investment.value = await MandateService.getMandateInvestment(
      mandate.value.id,
      limit.value,
      page.value
    )

    total.value = investment.value.total;
    maxPages.value = investment.value.maxPages;
  } finally {
    loading.value = false;
  }
}

const managementReportDocs = computed(() =>
  mandate.value.documents.filter(doc => doc.category === MandateDocumentCategoryEnum.MANAGEMENT_REPORT)
)

const managementReportHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.detailMandate.table.document.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.managementReport.reportPeriod"),
    key: "reportPeriod",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.document.createdAt"),
    key: "createdAt",
    align: "start",
    sortable: false
  },
  {
    title: t("views.detailMandate.table.document.action"),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])

const canUploadManagementReport = computed(() =>
  !authStore.isAdmin &&
  mandate.value.publicStatus === MandatePublicStatusEnum.OPENED
)

const uploadingReport = ref<boolean>(false)

async function addManagementReport(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(ManagementReportUploadComponent),
    props: {}
  })

  if (modal && typeof modal === 'object') {
    const { name, file, reportPeriod } = modal as { name: string; file: File; reportPeriod: string }
    try {
      uploadingReport.value = true
      await MandateService.uploadDocument(mandate.value.id, [{
        id: null,
        name,
        category: MandateDocumentCategoryEnum.MANAGEMENT_REPORT,
        created_at: new Date().toISOString(),
        file,
        reportPeriod
      }])
      mandate.value.documents.unshift({
        id: null,
        name,
        category: MandateDocumentCategoryEnum.MANAGEMENT_REPORT,
        created_at: new Date().toISOString(),
        file,
        reportPeriod
      })
      displayToast(t('views.detailMandate.managementReportUploadSuccess'), 'success')
    } catch {
      displayToast(t('views.detailMandate.managementReportUploadError'), 'error')
    } finally {
      uploadingReport.value = false
    }
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent>
    {{ t("views.detailMandate.title") }}

    <template #actions v-if="authStore.isAdmin">
      <v-col
        class="d-flex flex-column flex-sm-row"
        cols="12"
        sm="auto"
      >
        <v-btn
          v-if="canValidateMandate"
          :loading="loading"
          :disabled="loading"
          class="mr-0 mr-sm-4 mb-4 mb-sm-0"
          color="error"
          type="submit"
          variant="outlined"
          @click="refusedMandate()"
        >
          {{ t("views.detailMandate.buttons.refuse") }}
          <template #prepend>
            <v-icon icon="PhX" />
          </template>
        </v-btn>

        <v-btn
          v-if="canValidateMandate"
          :loading="loading"
          :disabled="loading"
          type="submit"
          @click="updateStatus('opened')"
        >
          {{ t("views.detailMandate.buttons.validate") }}
          <template #prepend>
            <v-icon icon="PhCheck" />
          </template>
        </v-btn>

        <v-btn
          v-if="canCloseMandate"
          :loading="loading"
          :disabled="loading"
          color="error"
          type="submit"
          variant="outlined"
          @click="updateStatus('private')"
        >
          {{ t("views.detailMandate.buttons.close") }}
          <template #prepend>
            <v-icon icon="PhLock" />
          </template>
        </v-btn>

        <v-btn
          v-if="canReactiveMandate"
          :disabled="loading"
          :loading="loading"
          type="submit"
          @click="updateStatus('opened')"
        >
          {{ t("views.detailMandate.buttons.reactivate") }}
          <template #prepend>
            <v-icon icon="PhLockOpen" />
          </template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-stepper v-model="step" :elevation="0" :mobile="smAndDown">
    <v-stepper-header>
      <v-divider />

      <v-stepper-item
        :title="t('views.detailMandate.stepper.step1')"
        :value="1"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.detailMandate.stepper.step2')"
        :value="2"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.detailMandate.stepper.step3')"
        :value="3"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.detailMandate.stepper.step4')"
        :value="4"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />

      <v-stepper-item
        v-if="!authStore.isAdmin"
        :title="t('views.detailMandate.stepper.step5')"
        :value="5"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />
    </v-stepper-header>

    <v-stepper-window>
      <!-- STEP 1 -->
      <v-stepper-window-item :value="1">
        <v-row align="center" justify="space-between">
          <v-col cols="8">
            <h2 class="text-h5 font-weight-bold mb-1">
              {{ mandate.name }}
            </h2>
            <span class="text-subtitle-2 text-grey">
              {{ mandate.author }}
            </span>
          </v-col>
          <v-col cols="4" class="text-right">
            <v-chip
              :color="formatMandateBoStatus(mandate.boStatus)"
              text-color="white"
              class="ma-2"
            >
              {{ t('views.detailMandate.boStatus.' + mandate.boStatus) }}
            </v-chip>
            <v-chip
              :color="formatMandatePublicStatus(mandate.publicStatus)"
              text-color="white"
              class="ma-2"
            >
              {{ t('views.detailMandate.publicStatus.' + mandate.publicStatus) }}
            </v-chip>
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col cols="12">
            <strong>{{ t('views.detailMandate.currency') }} :</strong>
            {{ formatItemValue(mandate.currency) }}
          </v-col>

          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.availableAt') }} :</strong>
            {{ formatDate(mandate.availableAt) }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.closedAt') }} :</strong>
            {{ formatDate(mandate.closedAt) }}
          </v-col>

          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.risk') }} :</strong>
            {{ mandate.risk }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.recommendedDuration') }} :</strong>
            {{ mandate.recommendedDuration }}
            {{ t('views.detailMandate.recommendedDurationSuffix', mandate.recommendedDuration) }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.minimumInvestment') }} :</strong>
            {{ formatCurrency(mandate.minimumInvestment) }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.maximumInvestment') }} :</strong>
            {{ formatCurrency(mandate.maximumInvestment) }}
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.description') }} :</strong>
            <p v-html="formatItemValue(mandate.description)" />
          </v-col>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.strategyOperation') }} :</strong>
            <p v-html="formatItemValue(mandate.strategyOperation)" />
          </v-col>

          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.presentation') }} :</strong>
            <p v-html="formatItemValue(mandate.presentation)" />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhYoutubeLogo"/>
              <h2 class="subtitle">{{ t("views.detailMandate.subtitle1") }}</h2>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="mandate.youtubeUrl">
          <v-col cols="12">
            <a :href="mandate.youtubeUrl" target="_blank">
              {{ mandate.youtubeUrl }}
            </a>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            {{ t('common.noData') }}
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 2 -->
      <v-stepper-window-item :value="2">
        <v-row align="center">
          <v-col cols="12" class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2" icon="PhMoney"/>
              <h2 class="subtitle mr-2">
                {{ t("views.detailMandate.subtitle2") }}
              </h2>
              <v-chip
                :color="totalPercentage === 100 ? 'success' : (totalPercentage > 100 ? 'error' : 'warning')"
                size="small"
              >
                {{ totalPercentage }}%
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="assetHeaders"
              :items="mandate.assets"
              :items-per-page="-1"
              :no-data-text="t('common.noResult')"
              class="rounded-std"
              hover
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ formatPercentage(item.percentage) }} %</td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 3 -->
      <v-stepper-window-item :value="3">
        <v-row>
          <v-col>
            <v-data-table
              :headers="documentHeaders"
              :items="mandate.documents.filter(doc => doc.category !== MandateDocumentCategoryEnum.MANAGEMENT_REPORT)"
              :items-per-page="-1"
              :no-data-text="t('common.noResult')"
              class="rounded-std"
              hover
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ categoryLabel(item.category) }}</td>
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>
                    <v-btn
                      variant="plain"
                      @click.stop.prevent="downloadDocument(item)"
                    >
                      <v-icon icon="PhDownload" />
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>

        <template v-if="!authStore.isAdmin">
          <DividerComponent />

          <v-row align="center" justify="space-between">
            <v-col class="d-flex align-center" cols="12" md="auto">
              <v-icon class="mr-2" color="primary" icon="PhFileText" />
              <h2 class="subtitle">{{ t('views.detailMandate.subtitleManagementReports') }}</h2>
            </v-col>
            <v-col v-if="canUploadManagementReport" cols="12" md="auto" class="d-flex justify-end">
              <v-btn
                :loading="uploadingReport"
                color="primary"
                variant="flat"
                @click="addManagementReport"
              >
                <template #prepend>
                  <v-icon icon="PhUpload" />
                </template>
                {{ t('views.detailMandate.buttons.uploadManagementReport') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-data-table
                :headers="managementReportHeaders"
                :items="managementReportDocs"
                :items-per-page="-1"
                :no-data-text="t('common.noResult')"
                class="rounded-std"
                hover
              >
                <template #item="{ item }">
                  <tr>
                    <td>{{ item.name }}</td>
                    <td>{{ item.reportPeriod ? formatDateOnly(item.reportPeriod) : '-' }}</td>
                    <td>{{ formatDate(item.created_at) }}</td>
                    <td>
                      <v-btn
                        v-if="item.id"
                        variant="plain"
                        @click.stop.prevent="downloadDocument(item)"
                      >
                        <v-icon icon="PhDownload" />
                      </v-btn>
                    </td>
                  </tr>
                </template>
                <template #bottom />
              </v-data-table>
            </v-col>
          </v-row>
        </template>
      </v-stepper-window-item>

      <!-- STEP 4 -->
      <v-stepper-window-item :value="4">
        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignIn"/>
            <h2 class="subtitle">{{ t("views.detailMandate.subtitle3") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.subscriptionFee') }} :</strong>
            {{ formatPercentage(mandate.fees.subscriptionFee) }} %
          </v-col>
        </v-row>
        <v-row v-if="mandate.fees.entryFeeTiers?.length">
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.entryFeeTiers') }} :</strong>
            <div v-for="(tier, index) in mandate.fees.entryFeeTiers" :key="index">
              {{ t('views.detailMandate.entryFeeTierLine', {
                operator: tier.operator,
                amount: Number(tier.amount).toLocaleString('fr-FR'),
                rate: Number(tier.feeRate).toLocaleString('fr-FR')
              }) }}
            </div>
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhReceipt"/>
            <h2 class="subtitle">{{ t("views.detailMandate.subtitle4") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.managementFee') }} :</strong>
            {{ formatPercentage(mandate.fees.managementFee) }} %
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.performanceFee') }} :</strong>
            {{ formatPercentage(mandate.fees.performanceFee) }} %
          </v-col>
        </v-row>
        <v-row v-if="mandate.fees.accountKeepingFee != null">
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.accountKeepingFee') }} :</strong>
            {{ formatPercentage(mandate.fees.accountKeepingFee) }} %
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignOut"/>
            <h2 class="subtitle">{{ t("views.detailMandate.subtitle5") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.exitFee') }} :</strong>
            {{ formatPercentage(mandate.fees.exitFee) }} %
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.earlyExitPenalty') }} :</strong>
            {{ formatPercentage(mandate.fees.earlyExitPenalty) }} %
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <strong>{{ t('views.detailMandate.earlyExitMinDuration') }} :</strong>
            {{ mandate.fees.earlyExitMinDuration }}
            {{ t('views.detailMandate.earlyExitMinDurationSuffix', mandate.fees.earlyExitMinDuration ?? 0) }}
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 5 -->
      <v-stepper-window-item :value="5">
        <v-row align="center" justify="space-between">
          <v-col class="d-flex align-center" cols="12" md="6">
            <v-alert
              color="grey-lighten-3"
              variant="outlined"
            >
              <div class="flex-column">
                <div class="alert-icon">
                  <v-icon icon="PhWallet" color="primary" size="large" />
                </div>
                <div class="text-black mt-5">
                  <p class="text-body-1 mb-2">
                    {{ t("views.detailMandate.totalInvestments") }}
                  </p>

                  <h1 class="title">{{ formatCurrency(investment.totalInvestment) }}</h1>
                </div>
              </div>
            </v-alert>
          </v-col>
          <v-col class="d-flex align-center" cols="12" md="6">
            <v-alert
              color="grey-lighten-3"
              variant="outlined"
            >
              <div class="flex-column">
                <div class="alert-icon">
                  <v-icon icon="PhUsers" color="primary" size="large" />
                </div>
                <div class="text-black mt-5">
                  <p class="text-body-1 mb-2">
                    {{ t("views.detailMandate.totalUserInvestments") }}
                  </p>

                  <h1 class="title">{{ investment.userInvestments.length }}</h1>
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhUserList"/>
            <h2 class="subtitle">{{ t("views.detailMandate.allocationByUser") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table-server
              :headers="userInvestmentHeaders"
              :items="investment.userInvestments"
              :loading="loading"
              :no-data-text="t('common.noResult')"
              :items-length="total"
              :items-per-page="limit"
              :page="page"
              hover
              @update:page="page = $event"
              @update:items-per-page="limit = $event"
            >
              <template #loading>
                <v-skeleton-loader type="table-row@4" />
              </template>
              <template #[`item.amount`]="{ item }">
                {{ formatCurrency(item.amount) }}
              </template>
              <template #[`item.firstName`]="{ item }">
                {{ item.first_name }}
              </template>
              <template #[`item.lastName`]="{ item }">
                {{ item.last_name }}
              </template>
              <template #bottom>
                <v-pagination
                  v-model="page"
                  :length="maxPages"
                  :tile="true"
                  density="compact"
                  total-visible="6"
                />
              </template>
            </v-data-table-server>
          </v-col>
        </v-row>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
</template>

<style scoped>
.v-alert {
  padding: 35px !important;
}

.alert-icon {
  padding: 10px;
  width: fit-content;
  border-radius: 12px
}
</style>
